import { IEditorV3, isIEditorV3 } from "@azu-business/editor-v3";
import {
  ArsListing,
  ArsListingBody,
  ArsTableRow,
  IRequestStatus,
  isArsListingBody,
  loadWrapper,
  MultipleDatasetCheck,
  SET_VARIABLES_IN_USE,
  useArsContext,
} from "aic";
import { CommentScope } from "components/common/comments/CommentScope";
import { v3EditorProps } from "components/common/v3EditorProps";
import { useAppSettings, useTlfContext } from "contexts/AppSettingsContext";
import { UPDATE_TLF } from "contexts/tlf/interface";
import {
  ArsListingCalculationDto,
  getArsListingCalculation,
} from "functions/api/tlfItem/getArsListingCalculation";
import { isTlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { startTransition, useEffect, useEffectEvent, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { ArsItemRequestingSpinner } from "./ArsItemRequestingSpinner";
import { ArsSetPicker } from "./ArsSetPicker";

/**
 * Wrapper component for ArsListing.
 */
export const TlfArsListing = () => {
  // Contexts
  const appSettings = useAppSettings();
  const tlfContext = useTlfContext();
  const tlfData = tlfContext.tlfData;
  const arsContext = useArsContext();

  const body =
    tlfContext && isTlfItemReadDto(tlfData) && isArsListingBody(tlfData.body, isIEditorV3)
      ? tlfData.body
      : null;
  const disabled = tlfContext.readOnly;

  // Component state items
  const [calculatedTableRows, setCalculatedTableRows] = useState<ArsTableRow<IEditorV3>[]>([]);
  const [requestingCalculation, setRequestingCalculation] = useState<
    IRequestStatus<ArsListingCalculationDto>
  >({
    requesting: false,
    error: false,
  });
  const [errorText, setErrorText] = useState<string | null>(null);

  const getCalculatedRows = useEffectEvent(async (body: ArsListingBody<IEditorV3>) => {
    const analysisSet = tlfData?.analysisSetId
      ? arsContext.state.analysisSets[tlfData.analysisSetId]
      : undefined;
    const dataSubset =
      body.dataSubsetId && Object.keys(arsContext.state.dataSubsets).includes(body.dataSubsetId)
        ? arsContext.state.dataSubsets[body.dataSubsetId]
        : undefined;
    const arsListingCalculationDto: ArsListingCalculationDto = {
      arsListingBody: body,
      analysisSet,
      dataSubset,
    };
    const request = await loadWrapper(
      getArsListingCalculation,
      arsListingCalculationDto,
      requestingCalculation,
      setRequestingCalculation,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (request?.success) {
      arsContext.dispatch({
        operation: SET_VARIABLES_IN_USE,
        variablesInUse: request.data.variables,
      });
      startTransition(() => {
        setCalculatedTableRows(request.data.rows);
        setErrorText(null);
      });
    } else if (request?.success === false) {
      startTransition(() => setErrorText(request.errorText));
    }
  });

  useEffect(() => {
    if (body) getCalculatedRows(body);
  }, [body]);

  return !tlfContext || !body ? null : (
    <Container style={{ minHeight: "400px" }}>
      {
        <>
          <Row>
            <Col className="d-flex align-items-start flex-row gap-1 py-3">
              <ArsSetPicker
                type={"analysisSet"}
                itemId={tlfData?.analysisSetId ?? ""}
                onChange={(newId) => {
                  tlfContext.dispatch({
                    operation: UPDATE_TLF,
                    tlfData: {
                      analysisSetId: newId,
                    },
                    calledFrom: "ArsListing onChange:AnalysisSet",
                  });
                }}
              />
              <ArsSetPicker
                type={"dataSubset"}
                itemId={body.dataSubsetId ?? ""}
                onChange={(newId) => {
                  tlfContext.dispatch({
                    operation: UPDATE_TLF,
                    tlfData: {
                      body: {
                        ...body,
                        dataSubsetId: newId,
                      },
                    },
                    calledFrom: "ArsListing onChange:DataSubset",
                  });
                }}
              />
              <MultipleDatasetCheck />
            </Col>
          </Row>
          {errorText && (
            <Row>
              <Col className="text-danger m-3">{errorText}</Col>
            </Row>
          )}
          <Row>
            <Col className="d-flex justify-content-center position-relative">
              <ArsItemRequestingSpinner
                requestingCalculation={requestingCalculation}
                requestingLabel="Recalculating"
              />
              <CommentScope
                id="ars-listing-comment"
                activeContext="TlfItem"
                wrap
                iconPosition={{ top: "-16px", left: "-16px" }}
                commentLabel="New table comment"
              >
                <ArsListing<IEditorV3>
                  arsListingBody={body}
                  calculatedTableRows={calculatedTableRows}
                  disabled={disabled}
                  editorProps={v3EditorProps}
                  justification="center"
                  sidebarStyle={{ paddingBottom: "40px" }}
                  style={appSettings.defaultTableStyle}
                  tlfViewMode={tlfContext.tlfViewMode}
                  updateArsListingBody={(newBody) =>
                    tlfContext!.dispatch({
                      operation: UPDATE_TLF,
                      tlfData: {
                        body: newBody as ArsListingBody<IEditorV3>,
                      },
                      calledFrom: "TlfArsListing onChange",
                    })
                  }
                />
              </CommentScope>
            </Col>
          </Row>
        </>
      }
    </Container>
  );
};

TlfArsListing.displayName = "TlfArsListing";
